import { AppTable, AppTableCell, AppTableRow } from '@components/AppTable';
import { ItemImage } from '@components/CatalogElements';
import { FileUploadDragAndDrop, type PickedFile } from '@components/FileUpload';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import { CenterDropdownStyle } from '@elements/Dropdown';
import { MuteLabel } from '@elements/FormField';
import { AppModal } from '@elements/Modal';
import { Checkbox, TextField } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import OrdersService from '@utility/services/order';
import UploadService from '@utility/services/upload';
import { ToastMessage } from '@utility/toast';
import { useEffect, useId, useState } from 'react';

function ItemRow({ item, qtyChanged, toggleItems }) {
  const maxQty = item.qty - (item.fulfilledQty || 0);
  const [localQty, setLocalQty] = useState<string>(
    String(item.newQty ?? maxQty),
  );

  const handleQtyChange = (value: string) => {
    setLocalQty(value);

    const numValue = +value;
    if (qtyChanged && numValue >= 1 && numValue <= maxQty) {
      qtyChanged(item, numValue);
    }
  };

  const handleBlur = () => {
    let qty = Number(localQty) || 1;
    if (qty < 1) {
      qty = 1;
    }

    if (qty > maxQty) {
      qty = maxQty;
    }

    setLocalQty(String(qty));

    if (qtyChanged) {
      qtyChanged(item, qty);
    }
  };

  return (
    <AppTableRow>
      <AppTableCell>
        <div className="sk:flex sk:items-center sk:gap-2">
          <Checkbox
            aria-label={`Fulfill ${item.p_sku || item.p_title || 'item'}`}
            isSelected={!!item.checked}
            onChange={() => toggleItems(item, !item.checked)}
          />
          <ItemImage item={item} size="mini" />
          <span>{item.p_title}</span>
        </div>
      </AppTableCell>
      <AppTableCell textAlign="center">{item.p_sku}</AppTableCell>
      <AppTableCell textAlign="center">
        <div className="sk:flex sk:flex-col sk:items-center sk:gap-0.5">
          <NumberMaskInput
            aria-label="Quantity"
            className="sk:w-16! sk:px-2! sk:text-center"
            maxValue={maxQty}
            minValue={1}
            value={localQty}
            onChange={handleQtyChange}
            onBlur={handleBlur}
          />
          <div className="sk:text-[11px] sk:text-fg-tertiary">
            Max: {maxQty}
          </div>
        </div>
      </AppTableCell>
    </AppTableRow>
  );
}

export function UploadShippingLabelPopup({
  orderItems,
  onClose,
  loading,
  autoFulfillerClicked,
  open,
  qtyChanged,
  toggleItems,
}) {
  const [files, setFiles] = useState<PickedFile[]>([]);
  const trackingLabelId = useId();
  const [processing, setProcessing] = useState(false);
  const [formValues, setFormValues] = useState({
    carrier_code: '',
    tracking_number: '',
  });

  const resetForms = () => {
    setFormValues({
      carrier_code: '',
      tracking_number: '',
    });
  };

  const onChangeForm = (key, value) => {
    setFormValues(formValue => ({
      ...formValue,
      [key]: value,
    }));
  };

  useEffect(() => {
    resetForms();
    setProcessing(false);
  }, [open]);

  const columns = [
    {
      text: 'Order Items',
      key: 'order_items',
      width: 5,
    },
    {
      text: 'SKU',
      key: 'sku',
      width: 2,
    },
    {
      text: 'Quantity',
      key: 'qty',
      width: 1,
    },
  ];

  const carrierOptions = [
    {
      text: 'DHL',
      value: 'dhl',
    },
    {
      text: 'FedEx',
      value: 'fedex',
    },
    {
      text: 'UPS',
      value: 'ups',
    },
    {
      text: 'USPS',
      value: 'usps',
    },
    {
      text: 'CANADA POST',
      value: 'CANADA POST',
    },
    {
      text: 'PUROLATOR',
      value: 'PUROLATOR',
    },
    {
      text: 'DPD',
      value: 'DPD',
    },
    {
      text: 'CANPAR',
      value: 'CANPAR',
    },
    {
      text: 'Other',
      value: 'other',
    },
  ];

  const onSubmit = async () => {
    const checkedItems = orderItems?.filter?.(item => item.checked);
    if (!CommonUtility.isValidArray(checkedItems)) {
      ToastMessage.error('No items selected for fulfillment');
      return;
    }

    if (!CommonUtility.isValidArray(files)) {
      ToastMessage.error('Attach a label');
      return;
    }

    if (!formValues.carrier_code) {
      ToastMessage.error('Enter a carrier code');
      return;
    }

    if (!formValues.tracking_number) {
      ToastMessage.error('Enter a tracking number');
      return;
    }

    try {
      setProcessing(true);

      const res = await OrdersService.getUploadLabelUrl(
        formValues.carrier_code,
      );

      await UploadService.media(
        res.uploadUrl,
        files[0]?.file,
        files[0]?.file.type,
      );

      const labelAttachment = {
        key: res.key,
        bucket_name: res.bucket,
        carrier_code: formValues.carrier_code,
        tracking_number: formValues.tracking_number,
      };

      const passed = await autoFulfillerClicked(false, labelAttachment);
      if (!passed) {
        setProcessing(false);
      }
    } catch (error) {
      setProcessing(false);
      ToastMessage.apiError(error);
    }
  };

  return (
    <AppModal
      open={open}
      closeModal={onClose}
      size="small"
      title="Upload Shipping Label"
      footer={
        <div className="sk:flex sk:justify-end sk:gap-2">
          <HoverBorderButton
            disabled={loading || processing}
            type="button"
            onClick={onClose}
          >
            Cancel
          </HoverBorderButton>
          <StyledButton
            onClick={onSubmit}
            loading={loading || processing}
            type="button"
          >
            Attach Label to Order & Fulfill
          </StyledButton>
        </div>
      }
    >
      <div className="sk:flex sk:flex-col sk:gap-3">
        <FileUploadDragAndDrop
          accept=".pdf"
          files={files}
          setFiles={setFiles}
          maxFiles={1}
        />
        <div className="sk:flex sk:flex-col sk:gap-1">
          <MuteLabel>Carrier Name</MuteLabel>
          <CenterDropdownStyle
            aria-label="Carrier Name"
            onChange={(e, { value }) => onChangeForm('carrier_code', value)}
            value={formValues.carrier_code}
            placeholder="Carrier Name"
            options={carrierOptions}
            selection
            sorted={false}
          />
        </div>
        <div className="sk:flex sk:flex-col sk:gap-1">
          <MuteLabel id={trackingLabelId}>Tracking Number</MuteLabel>
          <TextField
            aria-labelledby={trackingLabelId}
            className="sk:h-8"
            onChange={value => onChangeForm('tracking_number', value)}
            value={formValues.tracking_number}
            placeholder="Tracking Number"
          />
        </div>
        <div className="sk:max-h-[30vh] sk:overflow-y-auto">
          <AppTable
            // @ts-expect-error - TS2322: Type '{ text: string; key: string; width: number; }[]' is not assignable to type...
            columns={columns}
            RowComponent={
              // @ts-expect-error - TS2741: Property 'item' is missing in type '{}' but required in type '{ item: any; qtyChanged: any; }'.
              <ItemRow toggleItems={toggleItems} qtyChanged={qtyChanged} />
            }
            data={orderItems}
            noShadow
            noDataMessage="No Items to fulfill"
          />
        </div>
      </div>
    </AppModal>
  );
}
